import { WebGPUBackend } from "@tensorflow/tfjs-backend-webgpu"
import * as tf from "@tensorflow/tfjs"
import { debounce } from "@/utils/debounce"
import { Sentry } from "@/telemetry/sentry"
import { Adjustments } from "@/core/types"

export const CullingInputDimension = 1620 // max dimension for culling input image

export const AI_WORKER_REF_BITMAP_CACHE_MAX_SIZE = 100
export const AI_WORKER_REF_CACHE_MISS_ERROR =
	"No reference image data provided or cached in AI worker"

// Enable logging for different AI modules for debugging
const enableAILog = false // AI style processing overall
const enableCullingLog = false // AI culling
const enableLineDetectionLog = false // line detection
const enableClusteringLog = false // clustering
const enableImageAlignmentLog = false // image alignment
const enableAutoLightLog = false // auto lighting
// color match parameters estimation log is in src/lib/ai/color-match/utils.ts

// anything related to AI style processing
export function logAiInfo(...info: any) {
	if (enableAILog) {
		console.log("[AI]", ...info)
	}
}

export function logCullingInfo(...info: any) {
	if (enableCullingLog) {
		console.log("[culling]", ...info)
	}
}

export function logClustering(...info: any) {
	if (enableClusteringLog) {
		console.log("[cluster]", ...info)
	}
}

// for line detection and auto straightening & perspective correction
export function logLineDetection(...info: any) {
	if (enableLineDetectionLog) {
		console.log("[straighten]", ...info)
	}
}

export function logAutoLight(...info: any) {
	if (enableAutoLightLog) {
		console.log("[autoLight]", ...info)
	}
}

export function logImageAlignment(...info: any) {
	if (enableImageAlignmentLog) {
		console.log("[align]", ...info)
	}
}

export function cleanupWebGPUBuffers() {
	try {
		/* eslint-disable @typescript-eslint/no-unsafe-call */
		const backend = tf.engine().backend as WebGPUBackend

		// @ts-expect-error `freeBuffers` is private
		backend.bufferManager.freeBuffers.forEach((buffers) => {
			buffers.forEach((buffer) => {
				buffer.destroy()
				backend.bufferManager.numBytesAllocated -= buffer.size

				// @ts-expect-error `numFreeBuffers` is private
				backend.bufferManager.numFreeBuffers -= 1
			})
		})

		// @ts-expect-error `freeBuffers` is private
		backend.bufferManager.freeBuffers = new Map()
	} catch (e) {
		Sentry.captureException(new Error("WebGPU buffer cleanup failed", { cause: e }), {
			level: "warning",
		})
	}
}

export const cleanupWebGPUBuffersDebounced = debounce(cleanupWebGPUBuffers, 100)

export function areImageIdEquivalent(id1: string, id2: string): boolean {
	const parts1 = id1.split("|")
	const parts2 = id2.split("|")

	// Check if the first two segments of both strings are equal
	// e.g. NJ010086.ORF|19036416 and NJ010086.ORF|19036416|1716555570873
	return parts1[0] === parts2[0] && parts1[1] === parts2[1]
}

export function getGPUMemoryInfo() {
	const backend = tf.backend()
	const tfMemory = tf.memory()

	return {
		backend: tf.getBackend(),
		numTensors: tfMemory.numTensors,
		numDataBuffers: tfMemory.numDataBuffers,
		numBytes: tfMemory.numBytes,
		gpuBytes:
			backend instanceof WebGPUBackend ? backend.memory().numBytesAllocatedInGPU : undefined,
		unreliable: tfMemory.unreliable,
	}
}

// Yuhe: Debug helper functions
export function exportToJson(data: any, filename: string) {
	const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
	const url = URL.createObjectURL(blob)

	const a = document.createElement("a")
	a.href = url
	a.download = filename
	document.body.appendChild(a)
	a.click()

	// Cleanup
	setTimeout(() => {
		document.body.removeChild(a)
		URL.revokeObjectURL(url)
	}, 0)
}

export async function loadJsonFromPublic(filename: string): Promise<any> {
	try {
		const response = await fetch(`/${filename}`)
		if (!response.ok) {
			throw new Error(`Failed to load ${filename}: ${response.statusText}`)
		}
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return await response.json()
	} catch (error) {
		console.error("Error loading JSON:", error)
		throw error
	}
}

export async function debugSaveBitmap(bitmap: ImageBitmap, filename: string) {
	// Create a canvas and draw the bitmap
	const canvas = new OffscreenCanvas(bitmap.width, bitmap.height)
	const ctx = canvas.getContext("2d")
	if (!ctx) return
	ctx.drawImage(bitmap, 0, 0)

	// Convert canvas to blob
	const blob = await canvas.convertToBlob({ type: "image/png" })

	// Create download link
	const url = URL.createObjectURL(blob)
	const a = document.createElement("a")
	a.href = url
	a.download = filename
	document.body.appendChild(a)
	a.click()

	// Cleanup
	document.body.removeChild(a)
	URL.revokeObjectURL(url)
}

export function adjustmentsContainsNaN(adjustments: Partial<Adjustments>): boolean {
	return Object.entries(adjustments).some(
		([_, value]) => typeof value === "number" && Number.isNaN(value),
	)
}
